/*
 * This file is a clone of node-renderer-default.js from the 'react-sortable-tree' package
 * which has been brought into the project to allow us to make changes to the default template.
 * https://github.com/frontend-collective/react-sortable-tree/blob/master/src/node-renderer-default.js
 */

import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { classnames, isDescendant } from './utils'
import dayjs from 'dayjs'

class NavigationTreeRow extends Component {
    render() {
        const {
            scaffoldBlockPxWidth,
            toggleChildrenVisibility,
            connectDragPreview,
            connectDragSource,
            isDragging,
            canDrop,
            canDrag,
            node,
            title,
            linkText,
            subtitle,
            pathName,
            displayFrom,
            displayTo,
            draggedNode,
            path,
            treeIndex,
            isSearchMatch,
            isSearchFocus,
            buttons,
            className,
            style,
            didDrop,
            treeId,
            isOver, // Not needed, but preserved for other renderers
            parentNode, // Needed for dndManager
            rowDirection,
            isActiveRow, // Passed by @nosferatu500/react-sortable-tree, not a DOM attribute
            isSettling, // Passed by @nosferatu500/react-sortable-tree, not a DOM attribute
            ...otherProps
        } = this.props
        const nodeTitle = linkText || node.linkText
        const nodeSubtitle = pathName || node.pathName
        const nodeDisplayFrom = displayFrom || node.displayFrom
        const nodeDisplayTo = displayTo || node.displayTo
        const rowDirectionClass = rowDirection === 'rtl' ? 'rst__rtl' : null
        let dateRange

        if (nodeDisplayFrom && nodeDisplayTo) {
            dateRange = `${dayjs(nodeDisplayFrom)
                .add(dayjs().utcOffset())
                .format('DD/MM/YY HH:mm')}
                to ${dayjs(nodeDisplayTo)
                    .add(dayjs().utcOffset())
                    .format('DD/MM/YY HH:mm')}`
        }

        let handle
        if (canDrag) {
            if (typeof node.children === 'function' && node.expanded) {
                // Show a loading symbol on the handle when the children are expanded
                //  and yet still defined by a function (a callback to fetch the children)
                handle = (
                    <div className="rst__loadingHandle">
                        <div className="rst__loadingCircle">
                            {[...new Array(12)].map((_, index) => (
                                <div
                                    // eslint-disable-next-line react/no-array-index-key
                                    key={index}
                                    className={classnames(
                                        'rst__loadingCirclePoint',
                                        rowDirectionClass
                                    )}
                                />
                            ))}
                        </div>
                    </div>
                )
            } else {
                // Show the handle used to initiate a drag-and-drop
                // The react-dnd version used by the sortable tree only accepts connectors as refs
                handle = <div ref={connectDragSource} className="rst__moveHandle" />
            }
        }

        const isDraggedDescendant = draggedNode && isDescendant(draggedNode, node)
        const isLandingPadActive = !didDrop && isDragging

        let buttonStyle = { left: -0.5 * scaffoldBlockPxWidth }
        if (rowDirection === 'rtl') {
            buttonStyle = { right: -0.5 * scaffoldBlockPxWidth }
        }

        return (
            <div style={{ height: '100%' }} {...otherProps}>
                {toggleChildrenVisibility &&
                    node.children &&
                    (node.children.length > 0 || typeof node.children === 'function') && (
                        <div>
                            <button
                                type="button"
                                aria-label={node.expanded ? 'Collapse' : 'Expand'}
                                className={classnames(
                                    node.expanded ? 'rst__collapseButton' : 'rst__expandButton',
                                    rowDirectionClass
                                )}
                                style={buttonStyle}
                                onClick={() =>
                                    toggleChildrenVisibility({
                                        node,
                                        path,
                                        treeIndex,
                                    })
                                }
                            />

                            {node.expanded && !isDragging && (
                                <div
                                    style={{ width: scaffoldBlockPxWidth }}
                                    className={classnames('rst__lineChildren', rowDirectionClass)}
                                />
                            )}
                        </div>
                    )}

                <div className={classnames('rst__rowWrapper', rowDirectionClass)}>
                    {/* Set the row preview to be used during drag and drop */}
                    <div
                        ref={connectDragPreview}
                        className={classnames(
                            'rst__row',
                            isLandingPadActive && 'rst__rowLandingPad',
                            isLandingPadActive && !canDrop && 'rst__rowCancelPad',
                            isSearchMatch && 'rst__rowSearchMatch',
                            isSearchFocus && 'rst__rowSearchFocus',
                            rowDirectionClass,
                            className
                        )}
                        style={{
                            opacity: isDraggedDescendant ? 0.5 : 1,
                            ...style,
                        }}
                    >
                        {handle}

                        <div
                            className={classnames(
                                'rst__rowContents',
                                '!flex',
                                '!items-center',
                                'justify-conetent-between',
                                !canDrag && 'rst__rowContentsDragDisabled',
                                rowDirectionClass
                            )}
                        >
                            <div className={classnames('rst__rowLabel', rowDirectionClass)}>
                                <span className={classnames('rst__rowTitle')}>
                                    {typeof nodeTitle === 'function'
                                        ? nodeTitle({
                                              node,
                                              path,
                                              treeIndex,
                                          })
                                        : nodeTitle}

                                    {nodeSubtitle && (
                                        <>
                                            <span className="rst__rowSubtitle">
                                                {typeof nodeSubtitle === 'function'
                                                    ? ` (${nodeSubtitle({
                                                          node,
                                                          path,
                                                          treeIndex,
                                                      })})`
                                                    : ` (${nodeSubtitle})`}
                                            </span>
                                        </>
                                    )}
                                </span>
                            </div>

                            <div className="rst__rowToolbar">
                                <div className="rst__rowDateRange">
                                    {dateRange ? dateRange : '-'}
                                </div>
                                <div>
                                    {buttons.map((btn, index) => (
                                        <div
                                            key={index} // eslint-disable-line react/no-array-index-key
                                            className="rst__toolbarButton"
                                        >
                                            {btn}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        )
    }
}

NavigationTreeRow.defaultProps = {
    isSearchMatch: false,
    isSearchFocus: false,
    canDrag: false,
    toggleChildrenVisibility: null,
    buttons: [],
    className: '',
    style: {},
    parentNode: null,
    draggedNode: null,
    canDrop: false,
    title: null,
    subtitle: null,
    rowDirection: 'ltr',
}

NavigationTreeRow.propTypes = {
    node: PropTypes.shape({}).isRequired,
    title: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    subtitle: PropTypes.oneOfType([PropTypes.func, PropTypes.node]),
    path: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])).isRequired,
    treeIndex: PropTypes.number.isRequired,
    treeId: PropTypes.string.isRequired,
    isSearchMatch: PropTypes.bool,
    isSearchFocus: PropTypes.bool,
    canDrag: PropTypes.bool,
    scaffoldBlockPxWidth: PropTypes.number.isRequired,
    toggleChildrenVisibility: PropTypes.func,
    buttons: PropTypes.arrayOf(PropTypes.node),
    className: PropTypes.string,
    style: PropTypes.shape({}),

    // Drag and drop API functions
    // Drag source
    connectDragPreview: PropTypes.func.isRequired,
    connectDragSource: PropTypes.func.isRequired,
    parentNode: PropTypes.shape({}), // Needed for dndManager
    isDragging: PropTypes.bool.isRequired,
    didDrop: PropTypes.bool.isRequired,
    draggedNode: PropTypes.shape({}),
    // Drop target
    isOver: PropTypes.bool.isRequired,
    canDrop: PropTypes.bool,

    // rtl support
    rowDirection: PropTypes.string,
}

export default NavigationTreeRow
